import { getContentTypeListOfContent, postContentTypeListOfContent } from '@/apis/ContentTypeAPIs';
import { get } from '@/apis/client';
import { ContentItem, PaginatedResponse, Payload } from '@/app/types';

/**
 * Fetch hub page content filtered by tag and content type
 * Example: /contents/?tag=ai&content_type=blogs&page=1&size=12&sort=newest
 */
const getHubpageContent = async (params: {
  tag?: string;
  content_type?: string;
  page?: number | string;
  size?: number | string;
  sort?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): Promise<any> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.tag) queryParams.append('tag', params.tag);
    // Only add content_type if it's not "all" or empty
    if (params.content_type && params.content_type !== 'all') {
      queryParams.append('content_type', params.content_type);
    }
    queryParams.append('page', String(params.page ?? 1));
    queryParams.append('size', String(params.size ?? 12));
    if (params.sort) queryParams.append('sort', params.sort);

    const endpoint = `contents/?${queryParams.toString()}`;
    const result = await get(endpoint);

    if (!result || typeof result !== 'object') {
      return { results: [], header: { totalResult: 0 } };
    }

    // Don't throw error, just return the result even if it has error flag
    // The calling function will handle empty results
    return result;
  } catch (error) {
    // Return empty result instead of throwing
    return { results: [], header: { totalResult: 0 } };
  }
};

export async function fetchContentListItems(
  slug: string,
  tagType: string,
  pageNumber: number = 1,
  pageSize: number = 12,
  isSortEnabled: boolean = false,
  sortOrder: string = '',
  sortField: string = 'publish_date',
): Promise<PaginatedResponse<ContentItem>> {
  const payload: Payload = {
    tag: tagType,
    page: pageNumber.toString(),
    size: pageSize.toString(),
  };

  if (isSortEnabled) {
    payload.sort = sortOrder;
  }
  let response;
  //If tagType exists, use getHubpageContent
  if (tagType) {
    const hubParams = {
      tag: tagType,
      content_type: slug,
      page: pageNumber,
      size: pageSize,
      sort: isSortEnabled ? sortOrder : undefined,
    };
    response = await getHubpageContent(hubParams);
  } else {
    response = await postContentTypeListOfContent(slug, payload);
  }

  const contentItems = Array.isArray(response?.results) ? response.results : [];

  // Apply sorting
  if (isSortEnabled && Array.isArray(contentItems)) {
    switch (sortOrder) {
      case 'newest':
        contentItems.sort((a, b) => (b[sortField] || '').localeCompare(a[sortField] || ''));
        break;
      case 'oldest':
        contentItems.sort((a, b) => (a[sortField] || '').localeCompare(b[sortField] || ''));
        break;
      case 'title_ascending':
        contentItems.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'title_descending':
        contentItems.sort((a, b) => b.title.localeCompare(a.title));
        break;
      default:
        contentItems.sort((a, b) => (b[sortField] || '').localeCompare(a[sortField] || ''));
        break;
    }
  }
  const totalItems = response?.header?.totalResult || 0;
  return {
    data: contentItems,
    totalItems,
    totalPages: pageSize > 0 ? Math.ceil(totalItems / pageSize) : 0,
  };
}
